/**
 * POS-I266: pure helpers of the add-on screen (platform Subscription dialog and the tenant request basket).
 *
 * Nothing here prices a bill or computes an effective limit: the monthly figures and the new limits come from the
 * server preview (POST .../preview), which uses the same addonLineTotal / computeEffectiveLimits as the bill and the
 * enforcement. These helpers only group, filter, diff and word things.
 */

export type AddonGroupKey = 'sales' | 'stock' | 'finance' | 'restaurant' | 'integrations' | 'capacity' | 'other';

export const ADDON_GROUP_ORDER: readonly AddonGroupKey[] = ['sales', 'stock', 'finance', 'restaurant', 'integrations', 'capacity', 'other'];

export interface BasketAddon {
  id: string;
  key: string;
  name: string;
  description?: string | null;
  priceMonthly: number | string | null;
  type: string;
  limitType?: string | null;
  limitPerUnit?: number | null;
  unit?: string | null;
  isActive?: boolean;
  permissionKeys?: string[] | null;
  isPassThrough?: boolean;
  providerName?: string | null;
}

const GROUP_BY_KEY: Record<string, AddonGroupKey> = {
  // Sales and customers
  loyalty_program: 'sales', gift_cards: 'sales', cashier_shifts: 'sales', sales_returns: 'sales', price_tiers: 'sales',
  customer_portal: 'sales', installments_bnpl: 'sales', fbr_di: 'sales', tax_management: 'sales',
  // Stock and purchasing
  inventory_purchases: 'stock', stock_transfers: 'stock', advanced_costing: 'stock', import_templates: 'stock',
  barcode_labels: 'stock', expiry_tracking: 'stock', serial_number_tracking: 'stock', recipes_production: 'stock',
  vendor_payment_approvals: 'stock',
  // Finance and people
  accounting_reports: 'finance', bank_reconciliation: 'finance', expense_tracking: 'finance', cheque_management: 'finance',
  multi_currency: 'finance', payroll: 'finance', time_attendance: 'finance', compliance_audit: 'finance',
  // Restaurant
  restaurant_management: 'restaurant', table_reservations: 'restaurant', delivery_management: 'restaurant',
  online_ordering: 'restaurant', car_takeaway: 'restaurant',
  // Integrations and platform
  sms_alerts: 'integrations', api_access: 'integrations', dedicated_database: 'integrations',
};

/** The bucket of an add-on: every QUANTITY add-on is "Capacity"; modules are bucketed by key (unknown key = "More"). */
export function addonGroup(addon: Pick<BasketAddon, 'key' | 'type'>): AddonGroupKey {
  if (addon.type === 'QUANTITY') return 'capacity';
  return GROUP_BY_KEY[addon.key] ?? 'other';
}

/** 'max_mobile_links' -> 'maxMobileLinks' (the key of the server limits object). */
export function camelLimit(limitType: string): string {
  return limitType.replace(/_([a-z])/g, (_m, c: string) => c.toUpperCase());
}

/**
 * One Mobile Links pack also adds live phone sessions. Mirrors MOBILE_SESSIONS_PER_LINK_PACK in the backend
 * (trial-access.ts) for the WORDS on the card only; the figures in the summary come from the server preview.
 */
export const MOBILE_SESSIONS_PER_LINK_PACK = 2;

export interface LimitEffect {
  /** server limit key, e.g. 'maxMobileLinks' */
  limitKey: string;
  /** translation key suffix: the limitType, e.g. 'max_mobile_links' */
  limitType: string;
  perUnit: number;
}

/** What one unit of a QUANTITY add-on adds, as limits (Mobile Links adds two: links and phone sessions). */
export function limitEffects(addon: Pick<BasketAddon, 'type' | 'limitType' | 'limitPerUnit'>): LimitEffect[] {
  if (addon.type !== 'QUANTITY' || !addon.limitType) return [];
  const perUnit = addon.limitPerUnit ?? 1;
  const effects: LimitEffect[] = [{ limitKey: camelLimit(addon.limitType), limitType: addon.limitType, perUnit }];
  if (addon.limitType === 'max_mobile_links') {
    effects.push({ limitKey: 'maxMobileSessions', limitType: 'max_mobile_sessions', perUnit: MOBILE_SESSIONS_PER_LINK_PACK });
  }
  return effects;
}

/** "limit X -> Y" of one effect when the quantity goes from `fromQty` to `toQty`; to = null when the limit is unlimited. */
export function limitFromTo(currentLimit: number | null | undefined, effect: LimitEffect, fromQty: number, toQty: number): { from: number | null; to: number | null } | null {
  if (currentLimit === undefined) return null;
  if (currentLimit === null) return { from: null, to: null };
  return { from: currentLimit, to: Math.max(0, currentLimit + (toQty - fromQty) * effect.perUnit) };
}

/** true when every permission key of the add-on is already covered by the plan's keys (cannot be bought again). */
export function isIncludedInPlan(addon: Pick<BasketAddon, 'type' | 'permissionKeys'>, planKeys: readonly string[] | null | undefined): boolean {
  if (addon.type !== 'MODULE') return false;
  const keys = (addon.permissionKeys ?? []).filter((k) => k !== '*');
  if (keys.length === 0 || !planKeys || planKeys.length === 0) return false;
  if (planKeys.includes('*')) return true;
  const set = new Set(planKeys);
  return keys.every((k) => set.has(k) || set.has(`${k.split(':')[0]}:*`));
}

export type AddonStatus = 'active' | 'included' | 'available';

export function addonStatus(ownedQty: number, includedInPlan: boolean): AddonStatus {
  if (includedInPlan) return 'included';
  return ownedQty > 0 ? 'active' : 'available';
}

export type StatusFilter = 'all' | 'active' | 'available';

export interface AddonFilter {
  search: string;
  group: AddonGroupKey | 'all';
  status: StatusFilter;
}

export const EMPTY_FILTER: AddonFilter = { search: '', group: 'all', status: 'all' };

export function isFilterActive(f: AddonFilter): boolean {
  return f.search.trim() !== '' || f.group !== 'all' || f.status !== 'all';
}

export interface FilterContext {
  /** quantity of each add-on in the CURRENT selection (draft), by add-on id */
  qtyById: ReadonlyMap<string, number> | Record<string, number>;
  /** display name (translated); defaults to addon.name */
  labelOf?: (addon: BasketAddon) => string;
}

function qtyOf(ctx: FilterContext, id: string): number {
  const q = ctx.qtyById;
  return (q instanceof Map ? q.get(id) : (q as Record<string, number>)[id]) ?? 0;
}

/** Search (name, description, key), group chip and status filter. "Active" = in the draft selection. */
export function filterAddons<T extends BasketAddon>(addons: readonly T[], filter: AddonFilter, ctx: FilterContext): T[] {
  const needle = filter.search.trim().toLowerCase();
  return addons.filter((a) => {
    if (filter.group !== 'all' && addonGroup(a) !== filter.group) return false;
    if (filter.status === 'active' && qtyOf(ctx, a.id) <= 0) return false;
    if (filter.status === 'available' && qtyOf(ctx, a.id) > 0) return false;
    if (!needle) return true;
    const label = (ctx.labelOf ? ctx.labelOf(a) : a.name).toLowerCase();
    return [label, a.name, a.description ?? '', a.key.replace(/_/g, ' ')].some((s) => s.toLowerCase().includes(needle));
  });
}

export interface AddonSection<T> {
  group: AddonGroupKey;
  items: T[];
}

/** Group in the fixed bucket order; empty buckets are dropped; items keep their order. */
export function groupAddons<T extends BasketAddon>(addons: readonly T[]): AddonSection<T>[] {
  const buckets = new Map<AddonGroupKey, T[]>();
  for (const a of addons) {
    const g = addonGroup(a);
    buckets.set(g, [...(buckets.get(g) ?? []), a]);
  }
  return ADDON_GROUP_ORDER.filter((g) => buckets.has(g)).map((g) => ({ group: g, items: buckets.get(g)! }));
}

export type ChangeKind = 'add' | 'remove' | 'qty';

export interface BasketChange {
  addonId: string;
  kind: ChangeKind;
  from: number;
  to: number;
}

/** Pending changes between the saved selection and the draft: added, removed, quantity from -> to. */
export function diffSelection(saved: Record<string, number>, draft: Record<string, number>): BasketChange[] {
  const ids = new Set([...Object.keys(saved), ...Object.keys(draft)]);
  const out: BasketChange[] = [];
  for (const id of ids) {
    const from = saved[id] ?? 0;
    const to = draft[id] ?? 0;
    if (from === to) continue;
    out.push({ addonId: id, kind: from === 0 ? 'add' : to === 0 ? 'remove' : 'qty', from, to });
  }
  return out;
}

/** Selection map -> assign payload (quantity > 0 only). */
export function toAddonQuantities(selection: Record<string, number>): Array<{ addonId: string; quantity: number }> {
  return Object.entries(selection)
    .filter(([, quantity]) => quantity > 0)
    .map(([addonId, quantity]) => ({ addonId, quantity }));
}

export interface UsageWarning {
  limitKey: string;
  used: number;
  max: number;
}

/** Limits (server preview) below what is already in use: the new plan would put the business over its limit. */
export function usageWarnings(limits: Record<string, number | null | undefined> | null | undefined, used: Record<string, number | undefined>): UsageWarning[] {
  if (!limits) return [];
  const out: UsageWarning[] = [];
  for (const [limitKey, usedValue] of Object.entries(used)) {
    const max = limits[limitKey];
    if (usedValue === undefined || max === null || max === undefined) continue;
    if (usedValue > max) out.push({ limitKey, used: usedValue, max });
  }
  return out;
}
